import { Dispatch } from "redux";
import { find } from "lodash";
import {
  CaseFollow,
  Meta,
  Case,
  CaseFollowAttributes,
  DistributionListAttributes,
  DistributionList,
} from "typings";
import { Users, CaseFollows, DistributionLists } from "@pacerpro/api";
import { RootState } from ".";

type CaseFollowsPayload = {
  data: CaseFollow[];
  included: any[];
  meta: Meta;
};

const noop = () => {};

/////////////////////////////////////////////////
// USER CASE FOLLOWS CONSTANTS
/////////////////////////////////////////////////

export const LOAD_USER_CASE_FOLLOWS = "LOAD_USER_CASE_FOLLOWS";
export const LOAD_MORE_USER_CASE_FOLLOWS = "LOAD_MORE_USER_CASE_FOLLOWS";
export const UNLOAD_USER_CASE_FOLLOWS = "UNLOAD_USER_CASE_FOLLOWS";
export const ADD_USER_CASE_FOLLOW_TO_LIST = "ADD_USER_CASE_FOLLOW_TO_LIST";
export const EDIT_USER_CASE_FOLLOWS_LIST = "EDIT_USER_CASE_FOLLOWS_LIST";
export const EDIT_USER_CASE_FOLLOWS_INCLUDED =
  "EDIT_USER_CASE_FOLLOWS_INCLUDED";
export const EDIT_ALL_USER_CASE_FOLLOWS_LIST =
  "EDIT_ALL_USER_CASE_FOLLOWS_LIST";
export const REMOVE_USER_CASE_FOLLOW_FROM_LIST =
  "REMOVE_USER_CASE_FOLLOW_FROM_LIST";
export const SET_USER_CASE_FOLLOWS_LOADING = "SET_USER_CASE_FOLLOWS_LOADING";
export const SET_USER_CASE_FOLLOWS_PAGE = "SET_USER_CASE_FOLLOWS_PAGE";

/////////////////////////////////////////////////
// USER CASE FOLLOWS ACTIONS
/////////////////////////////////////////////////

export const loadUserCaseFollows = ({
  data = [],
  included = [],
  meta,
}: CaseFollowsPayload) => ({
  type: LOAD_USER_CASE_FOLLOWS,
  payload: { data, included, meta },
});

export const unloadUserCaseFollows = () => ({
  type: UNLOAD_USER_CASE_FOLLOWS,
});

export const addUserCaseFollowToList = (payload: {
  data: CaseFollow;
  included: any[];
}) => ({
  type: ADD_USER_CASE_FOLLOW_TO_LIST,
  payload,
});

export const editUserCaseFollowsList = (payload: CaseFollow) => ({
  type: EDIT_USER_CASE_FOLLOWS_LIST,
  payload,
});

export const editUserCaseFollowsIncluded = (
  payload: DistributionList | Case,
) => ({
  type: EDIT_USER_CASE_FOLLOWS_INCLUDED,
  payload,
});

export const editAllUserCaseFollowsList = (
  payload: {
    data: {
      data: CaseFollow;
    };
  }[],
) => ({
  type: EDIT_ALL_USER_CASE_FOLLOWS_LIST,
  payload,
});

export const removeUserCaseFollowFromList = (caseFollowId: string) => ({
  type: REMOVE_USER_CASE_FOLLOW_FROM_LIST,
  caseFollowId,
});

export const setUserCaseFollowsLoading = (payload: boolean) => ({
  type: SET_USER_CASE_FOLLOWS_LOADING,
  payload,
});

export const setUserCaseFollowsPage = (payload: number) => ({
  type: SET_USER_CASE_FOLLOWS_PAGE,
  payload,
});

export const loadMoreUserCaseFollows = (
  userId: string,
  options: {
    include?: string[];
    size: number;
  },
) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const page = state.userCaseFollows.page;
  const pageCount = state.userCaseFollows.meta.pageCount;
  const isLoading = state.userCaseFollows.isLoading;
  if (isLoading) {
    return new Promise(noop);
  }
  if (page === pageCount) {
    return new Promise(noop);
  }
  try {
    dispatch(setUserCaseFollowsLoading(true));
    const userCaseFollowsResponse = await Users.getCaseFollows(userId, {
      ...options,
      number: page + 1,
    });
    dispatch({
      type: LOAD_MORE_USER_CASE_FOLLOWS,
      payload: userCaseFollowsResponse.data,
    });
    dispatch(setUserCaseFollowsLoading(false));
    dispatch(setUserCaseFollowsPage(page + 1));
  } catch (error) {
    console.error(error);
  }
};

export const createUserCaseFollow = (payload: {
  userId: string;
  caseId: string;
  state: string;
}) => async (dispatch: Dispatch) => {
  try {
    const caseFollowResponse = await CaseFollows.create(
      {
        userId: payload.userId,
        caseId: payload.caseId,
        state: payload.state,
      },
      { include: ["distributionList", "case"] },
    );
    dispatch(addUserCaseFollowToList(caseFollowResponse.data));
  } catch (error) {
    console.error(error);
  }
};

export const patchUserCaseFollow = (
  caseFollowId: string,
  caseFollowAttributes: CaseFollowAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const caseFollowsResponse = await CaseFollows.edit(
      caseFollowId,
      caseFollowAttributes,
      {
        include: ["distributionList", "case"],
      },
    );
    dispatch(addUserCaseFollowToList(caseFollowsResponse.data));
  } catch (error) {
    console.error(error);
  }
};

export const editUserCaseFollow = (
  caseFollowId: string,
  caseFollowAttributes: CaseFollowAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const caseFollowResponse = await CaseFollows.edit(
      caseFollowId,
      caseFollowAttributes,
    );
    return dispatch(editUserCaseFollowsList(caseFollowResponse.data.data));
  } catch (error) {
    console.error(error);
  }
};

export const editUserDistributionList = (
  distributionListId: string,
  distributionListAttributes: DistributionListAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const distributionListResponse = await DistributionLists.edit(
      distributionListId,
      distributionListAttributes,
    );
    return dispatch(
      editUserCaseFollowsIncluded(distributionListResponse.data.data),
    );
  } catch (error) {
    console.error(error);
  }
};

export const editAllUserCaseFollows = (payload: Promise<any>[]) => async (
  dispatch: Dispatch,
) => {
  try {
    const caseFollowsResponse = await Promise.all(payload);
    dispatch(editAllUserCaseFollowsList(caseFollowsResponse));
  } catch (error) {
    console.error(error);
  }
};

export const removeUserCaseFollow = (caseFollowId: string) => async (
  dispatch: Dispatch,
) => {
  try {
    const removedCaseFollow = await CaseFollows.remove(caseFollowId);
    return dispatch(
      removeUserCaseFollowFromList(removedCaseFollow.data.data.id),
    );
  } catch (error) {
    console.error(error);
  }
};

export type UserCaseFollowAction =
  | {
      type: typeof LOAD_USER_CASE_FOLLOWS;
      payload: CaseFollowsPayload;
    }
  | {
      type: typeof LOAD_MORE_USER_CASE_FOLLOWS;
      payload: CaseFollowsPayload;
    }
  | {
      type: typeof UNLOAD_USER_CASE_FOLLOWS;
    }
  | {
      type: typeof EDIT_USER_CASE_FOLLOWS_LIST;
      payload: CaseFollow;
    }
  | {
      type: typeof EDIT_ALL_USER_CASE_FOLLOWS_LIST;
      payload: { data: { data: CaseFollow } }[];
    }
  | {
      type: typeof EDIT_USER_CASE_FOLLOWS_INCLUDED;
      payload: DistributionList | Case;
    }
  | {
      type: typeof REMOVE_USER_CASE_FOLLOW_FROM_LIST;
      caseFollowId: string;
    }
  | {
      type: typeof ADD_USER_CASE_FOLLOW_TO_LIST;
      payload: {
        data: CaseFollow;
        included: any[];
      };
    }
  | {
      type: typeof SET_USER_CASE_FOLLOWS_PAGE;
      payload: number;
    }
  | {
      type: typeof SET_USER_CASE_FOLLOWS_LOADING;
      payload: boolean;
    };

/////////////////////////////////////////////////
// CASE FOLLOWS REDUCER
/////////////////////////////////////////////////

export interface UserCaseFollowState {
  isLoading: boolean;
  page: number;
  userCaseFollows: CaseFollow[];
  included: any[];
  meta: Meta;
}

const initialState: UserCaseFollowState = {
  isLoading: false,
  page: 1,
  userCaseFollows: [],
  meta: {
    recordCount: 0,
    pageCount: 0,
  },
  included: [],
};

export default (state = initialState, action: UserCaseFollowAction) => {
  switch (action.type) {
    case LOAD_USER_CASE_FOLLOWS: {
      return {
        ...state,
        isLoading: false,
        page: 1,
        userCaseFollows: action.payload.data,
        included: action.payload.included,
        meta: action.payload.meta,
      };
    }
    case LOAD_MORE_USER_CASE_FOLLOWS: {
      return {
        ...state,
        userCaseFollows: [...state.userCaseFollows, ...action.payload.data],
        included: [...state.included, ...action.payload.included],
      };
    }
    case UNLOAD_USER_CASE_FOLLOWS: {
      return {
        ...initialState,
      };
    }
    case ADD_USER_CASE_FOLLOW_TO_LIST: {
      return {
        ...state,
        userCaseFollows: [action.payload.data, ...state.userCaseFollows],
        included: [...state.included, ...action.payload.included],
      };
    }
    case EDIT_USER_CASE_FOLLOWS_LIST: {
      return {
        ...state,
        userCaseFollows: state.userCaseFollows.map(userCaseFollow =>
          userCaseFollow.id === action.payload.id
            ? {
                ...userCaseFollow,
                attributes: {
                  ...userCaseFollow.attributes,
                  ...action.payload.attributes,
                },
              }
            : userCaseFollow,
        ),
      };
    }
    case EDIT_ALL_USER_CASE_FOLLOWS_LIST: {
      return {
        ...state,
        userCaseFollows: state.userCaseFollows.map(userCaseFollow => {
          const newCaseFollow = find(
            action.payload,
            caseFollow =>
              caseFollow &&
              caseFollow.data &&
              caseFollow.data.data.id === userCaseFollow.id,
          );
          if (newCaseFollow) {
            return {
              ...userCaseFollow,
              attributes: {
                ...userCaseFollow.attributes,
                ...newCaseFollow.data.data.attributes,
              },
            };
          }
          return userCaseFollow;
        }),
      };
    }
    case SET_USER_CASE_FOLLOWS_PAGE: {
      return {
        ...state,
        page: action.payload,
      };
    }
    case SET_USER_CASE_FOLLOWS_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    case EDIT_USER_CASE_FOLLOWS_INCLUDED: {
      return {
        ...state,
        included:
          state.included &&
          state.included.map(related =>
            related.id === action.payload.id ? action.payload : related,
          ),
      };
    }
    case REMOVE_USER_CASE_FOLLOW_FROM_LIST: {
      return {
        ...state,
        userCaseFollows: state.userCaseFollows.filter(
          userCaseFollow => userCaseFollow.id !== action.caseFollowId,
        ),
      };
    }
    default:
      return state;
  }
};
